'use client';

import { useTheme } from '@emotion/react';
import { useEffect, useState } from 'react';
import { fetchViewed } from '../../../utils/viewed';
import { http } from '../../../lib/api/http';
import { V2 } from '../../../lib/api/urls';
import { DEVICE_SCREENS_MAX_STRING } from '../../../lib/consts';
import { BreadCrumbs } from '../../blocks/BreadCrumbs';
import { H1 } from '../../styled/typography';
import { Container } from '../../layout/Container';
import { ShareBlock } from '../../blocks/ShareBlock';
import { ActionCardColumn } from '../../actions/ActionCardColumn';
import { Compilations } from '../../compilations/compilations';
import { Viewed } from '../../viewed/viewed';
import { Subscription } from '../../about/Subscription';
import { MobileApps } from '../../about/MobileApps';
import { UpButton } from '../../common/UpButton';
import ViewSVG from '../../../public/img/views2.svg';
import {
  CropDescriptionForCompilationPage,
  CropText,
} from '../../collapsable/collapsable';
import { ButtonShowMore } from '../../Buttons';
import { StandardButton } from '../../styled/Button';
import { activeStatus } from '../../../constants/events';
import { COMPILATION_EVENTS_COUNT } from '../../../utils/consts';
import { Action, City, Compilation } from '../../../models';
import {
  getLocalStorageItem,
  setLocalStorageItem,
} from '../../../lib/api/browserStorage';
import { getEvents } from '../../../lib/services/backend';
import { GB_DOMAIN } from '../../../lib/api/apiUrl';
import { ActionList } from '../../../models/action';

export function CompilationPage({
  currentCity,
  compilation,
  initialEventsList,
  compilationsList,
}: {
  currentCity: City;
  compilation: Compilation;
  compilationsList: { results: Compilation[]; next: string | null };
  initialEventsList: ActionList;
}) {
  const theme = useTheme();
  const [viewed, setViewed] = useState<{ results: Action[] }>({ results: [] });
  const [eventsList, setEventsList] = useState<ActionList | null>(null);
  const [isLoadingEvents, setIsLoadingEvents] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    setPage(1);
    setEventsList(initialEventsList);
  }, [compilation.id]);

  const showMoreEvents = async () => {
    if (eventsList) {
      setIsLoadingEvents(true);
      const newEventsList = await getEvents({
        ids: compilation.events.join(','),
        status: activeStatus,
        is_web: true,
        limit: COMPILATION_EVENTS_COUNT,
        offset: page * COMPILATION_EVENTS_COUNT,
      });
      setEventsList({
        ...newEventsList,
        results: eventsList.results.concat(newEventsList.results),
      });
      setPage(page + 1);
      setIsLoadingEvents(false);
    }
  };

  useEffect(() => {
    http.post(V2.eventViews, {
      Compilation: compilation.remote_id,
    });
  }, [compilation.id]);
  useEffect(() => {
    async function fetch() {
      const viewedList = await fetchViewed();
      setViewed(viewedList);
    }
    fetch();
    let viewedActions = [];
    viewedActions =
      (JSON.parse(getLocalStorageItem('viewed-events') || '[]') || []).slice(
        0,
        8
      ) || [];
    setLocalStorageItem('viewed-events', JSON.stringify([...viewedActions]));
  }, [compilation.id]);

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: compilation.title,
    description: compilation.description,
    image: [compilation.images[0].image_360],
    offers: initialEventsList.currency_stats.map((stat) => ({
      '@type': 'AggregateOffer',
      offerCount: stat.count,
      highPrice: stat.max_price,
      lowPrice: stat.min_price,
      priceCurrency: stat.price_currency,
      availability: 'https://schema.org/InStock',
      url: `${GB_DOMAIN}${
        compilation.cities.length === 1 ? `/${compilation.cities[0].url}` : ''
      }/compilations/${compilation.slug}/`,
    })),
  };

  return (
    <>
      <script
        type='application/ld+json'
        dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }}
      />
      <Container
        css={{
          padding: '16px 12px',
          [DEVICE_SCREENS_MAX_STRING.md]: {
            padding: '28px 8px 0 8px',
          },
        }}
      >
        <div
          css={{
            '> div': {
              [DEVICE_SCREENS_MAX_STRING.md]: {
                display: 'inline-flex',
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                display: 'none',
              },
            },
          }}
        >
          <BreadCrumbs
            currentCity={currentCity}
            items={[
              {
                title: 'Подборки',
                url: 'compilations',
              },
              { title: compilation.title },
            ]}
          />
          <H1
            css={{
              width: '100%',
              textAlign: 'center',
              fontFamily: 'Montserrat',
              lineHeight: '38px',
              letterSpacing: '0.01em',
              color: theme.colors.text,
              fontWeight: 700,
              fontSize: 32,
              marginTop: 16,
              marginBottom: '4rem',
              [DEVICE_SCREENS_MAX_STRING.md]: {
                textAlign: 'left',
                marginTop: '1.5rem',
                marginBottom: '2rem',
                fontSize: 32,
              },
              [DEVICE_SCREENS_MAX_STRING.sm]: {
                margin: '0.75em 0 0.75em',
                fontSize: 22,
                lineHeight: '26px',
              },
            }}
          >
            {compilation.title}
          </H1>
        </div>
        <CropDescriptionForCompilationPage
          text={compilation.lead_text}
          deskTopCount={250}
          mobileCount={250}
          images={compilation.images}
          compilation={compilation}
        />
        {!!compilation.views && (
          <div
            css={{
              fontFamily: 'Golos',
              display: 'flex',
              alignItems: 'center',
              gap: 5,
              margin: '20px 10%',
              color: '#888',
              fontWeight: 500,
            }}
          >
            <ViewSVG css={{ width: 20, height: 18, fill: '#BFC1C3' }} />{' '}
            {compilation.views}
          </div>
        )}
        <div
          css={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'flex-start',
            alignItems: 'flex-end',
            marginRight: '10%',
            marginBottom: 15,
            textAlign: 'right',
            span: {
              marginRight: 5,
            },
          }}
        >
          <ShareBlock item={compilation} />
        </div>
        <main css={{ fontFamily: 'Golos', color: theme.colors.black }}>
          <div
            // eslint-disable-next-line @typescript-eslint/ban-ts-comment
            // @ts-ignore
            css={{
              marginBottom: '4rem',
              ...theme.mixins.row(),
              [DEVICE_SCREENS_MAX_STRING.md]: {
                marginBottom: '2rem',
              },
            }}
          >
            <div
              // actions-column
              // eslint-disable-next-line @typescript-eslint/ban-ts-comment
              // @ts-ignore
              css={{
                ...theme.mixins.flex(),
              }}
            >
              {eventsList
                ? eventsList.results.map((item) => (
                    <ActionCardColumn
                      key={`actionCardHorizontal${item.id}`}
                      item={item}
                    />
                  ))
                : initialEventsList.results.map((item) => (
                    <ActionCardColumn
                      key={`actionCardHorizontal${item.id}`}
                      item={item}
                    />
                  ))}
            </div>
            {eventsList && eventsList.next && (
              <div
                css={{
                  width: '100%',
                  display: 'flex',
                  gap: 10,
                  padding: '0 8px',
                  [DEVICE_SCREENS_MAX_STRING.md]: {
                    paddingTop: 10,
                  },
                }}
              >
                <ButtonShowMore
                  callback={showMoreEvents}
                  loadingText={isLoadingEvents ? 'Загрузка...' : ''}
                />
                <StandardButton callback={() => window.scrollTo(0, 0)}>
                  Наверх
                </StandardButton>
              </div>
            )}
          </div>
          <Compilations
            title='Другие подборки'
            compilations={compilationsList}
            currentCity={currentCity}
            href={['', currentCity.url, 'compilations', ''].join('/')}
          />
          <Viewed title='Вы смотрели' viewed={viewed} />
          {compilation.description && (
            <div
              css={{
                marginBottom: 60,
                [DEVICE_SCREENS_MAX_STRING.xl]: {
                  paddingTop: '2rem',
                },
              }}
            >
              <CropText
                text={compilation.description}
                deskTopCount={1000}
                mobileCount={1000}
              />
            </div>
          )}
          <div
            css={{
              display: 'grid',
              gridTemplateColumns: '1fr 1fr',
              gap: 16,
              marginBottom: '4rem',
              [DEVICE_SCREENS_MAX_STRING.xl]: {
                gridTemplateColumns: '1fr',
                marginBottom: '2rem',
              },
            }}
          >
            <Subscription currentCity={currentCity} />
            <MobileApps />
          </div>
          <UpButton screenCount={1.5} />
        </main>
      </Container>
    </>
  );
}
